import {
  type ContractingEntity,
  type ContractType,
  type ExtractedFields,
  type ModelExtractedFields,
  ModelExtractedFieldsSchema,
} from "@contract-manager/shared";

/**
 * The approval form's inputs, and the trip between them and the API.
 *
 * Every input holds a string, because that is what an `<input>` gives back.
 * An empty one means "the contract does not state this", which the ledger
 * records as null (docs/mvp-design.md §4) — so a reviewer clears a field to
 * say the model invented it, rather than needing a separate control.
 */

/** Yes, no, or not stated: auto-renewal has three answers, not two. */
export type AutoRenewalAnswer = "yes" | "no" | "";

export interface ApprovalFormState {
  readonly title: string;
  readonly contractType: ContractType;
  readonly counterparty: string;
  readonly counterpartyShortName: string;
  readonly contractingEntity: ContractingEntity;
  readonly effectiveDate: string;
  readonly endDate: string;
  readonly autoRenewal: AutoRenewalAnswer;
  readonly renewalTermMonths: string;
  readonly renewalNoticePeriodDays: string;
  readonly amountValue: string;
  readonly amountCurrency: string;
}

export type ApprovalFormField = keyof ApprovalFormState;

export type ApprovalPayloadResult =
  | { readonly ok: true; readonly value: ModelExtractedFields }
  | { readonly ok: false; readonly invalid: readonly ApprovalFormField[] };

function textOf(value: string | number | null): string {
  return value === null ? "" : `${value}`;
}

/**
 * The deadline is left behind on purpose: it is derived from the end date and
 * the notice period, and the API derives it again from whatever is approved.
 */
export function fromExtracted(extracted: ExtractedFields): ApprovalFormState {
  return {
    title: extracted.title,
    contractType: extracted.contractType,
    counterparty: extracted.counterparty,
    counterpartyShortName: textOf(extracted.counterpartyShortName),
    contractingEntity: extracted.contractingEntity,
    effectiveDate: textOf(extracted.effectiveDate),
    endDate: textOf(extracted.endDate),
    autoRenewal:
      extracted.autoRenewal === null
        ? ""
        : extracted.autoRenewal
          ? "yes"
          : "no",
    renewalTermMonths: textOf(extracted.renewalTermMonths),
    renewalNoticePeriodDays: textOf(extracted.renewalNoticePeriodDays),
    amountValue: textOf(extracted.amount?.value ?? null),
    amountCurrency: extracted.amount?.currency ?? "",
  };
}

function stated(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** NaN for text that is not a number, so the schema rejects it by name. */
function statedNumber(value: string): number | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : Number(trimmed);
}

/**
 * Thousands separators, ASCII or full-width, as an amount is written on the
 * contract ("1,200,000"). Dropped before parsing: they carry no value, and
 * refusing them would make a reviewer retype what they copied from the PDF.
 */
const THOUSANDS_SEPARATORS = /[,，]/g;

function statedAmount(value: string): number | null {
  return statedNumber(value.replace(THOUSANDS_SEPARATORS, ""));
}

/** Where a schema issue lands on the form; amount is two inputs. */
function fieldOf(path: readonly PropertyKey[]): ApprovalFormField {
  if (path[0] === "amount") {
    return path[1] === "currency" ? "amountCurrency" : "amountValue";
  }
  return path[0] as ApprovalFormField;
}

/**
 * Validated against the same schema the API parses with, so a value this
 * accepts is one the API accepts — the form cannot drift into its own rules.
 */
export function toApprovalPayload(
  state: ApprovalFormState,
): ApprovalPayloadResult {
  const amountValue = statedAmount(state.amountValue);
  const amountCurrency = stated(state.amountCurrency)?.toUpperCase() ?? null;

  // Half an amount is not "not stated": refuse it rather than drop the half
  // the reviewer did fill in.
  if ((amountValue === null) !== (amountCurrency === null)) {
    return {
      ok: false,
      invalid: [amountValue === null ? "amountValue" : "amountCurrency"],
    };
  }

  const candidate = {
    title: state.title.trim(),
    contractType: state.contractType,
    counterparty: state.counterparty.trim(),
    counterpartyShortName: stated(state.counterpartyShortName),
    contractingEntity: state.contractingEntity,
    effectiveDate: stated(state.effectiveDate),
    endDate: stated(state.endDate),
    autoRenewal: state.autoRenewal === "" ? null : state.autoRenewal === "yes",
    renewalTermMonths: statedNumber(state.renewalTermMonths),
    renewalNoticePeriodDays: statedNumber(state.renewalNoticePeriodDays),
    amount:
      amountValue === null || amountCurrency === null
        ? null
        : { value: amountValue, currency: amountCurrency },
  };

  const parsed = ModelExtractedFieldsSchema.safeParse(candidate);
  if (parsed.success) {
    return { ok: true, value: parsed.data };
  }
  const invalid = new Set(
    parsed.error.issues.map((issue) => fieldOf(issue.path)),
  );
  return { ok: false, invalid: [...invalid] };
}
